import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, RefreshCw, MessageSquare, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import api from '../utils/api';

// Helper to parse inline markdown (bold, emojis)
const parseInlineMarkdown = (text) => {
  if (!text) return text;

  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    if (boldMatch) {
      const beforeBold = remaining.substring(0, boldMatch.index);
      if (beforeBold) parts.push(<span key={key++}>{beforeBold}</span>);
      parts.push(<strong key={key++} className="font-semibold text-white">{boldMatch[1]}</strong>);
      remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return parts.length > 0 ? parts : text;
};

// Detect metric comparison lines like "Belægning: 77.7% vs. gns. 87.2% (-10.9%)"
const parseMetricLine = (text) => {
  const m = text.match(/^([^:]+):\s*([^()]+?)\s*\(([+-]?\d+(?:[.,]\d+)?%)\)\s*(.*)$/);
  if (!m) return null;
  const pct = m[3];
  const isPositive = pct.startsWith('+');
  const isNegative = pct.startsWith('-');
  return {
    label: m[1].trim(),
    value: m[2].trim(),
    delta: pct,
    tail: m[4].trim(),
    direction: isPositive ? 'up' : isNegative ? 'down' : 'neutral'
  };
};

// Parse markdown-like formatting to JSX — clean, professional layout
const formatAnalysis = (text) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let listMode = 'bullet'; // 'bullet' | 'metric'

  const flushList = () => {
    if (currentList.length === 0) return;
    if (listMode === 'metric') {
      elements.push(
        <div key={`mlist-${elements.length}`} className="space-y-1.5 mb-4">
          {currentList.map((m, i) => {
            const color =
              m.direction === 'up' ? 'text-emerald-400' :
              m.direction === 'down' ? 'text-rose-400' :
              'text-white/60';
            const icon = m.direction === 'up' ? '↑' : m.direction === 'down' ? '↓' : '≈';
            return (
              <div key={i} className="flex items-baseline justify-between gap-3 px-3 py-2 bg-white/[0.02] rounded border border-white/[0.04]">
                <div className="text-white/80 text-sm">
                  <span className="text-white/90">{m.label}</span>
                  <span className="text-white/50 ml-2">{m.value}</span>
                  {m.tail && <span className="text-white/40 ml-2 text-xs">{parseInlineMarkdown(m.tail)}</span>}
                </div>
                <div className={`text-sm font-semibold tabular-nums ${color}`}>{icon} {m.delta}</div>
              </div>
            );
          })}
        </div>
      );
    } else {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-1.5 mb-4 ml-1">
          {currentList.map((item, i) => (
            <li key={i} className="text-white/75 text-sm leading-relaxed flex gap-2">
              <span className="text-purple-400/70 mt-0.5">•</span>
              <span className="flex-1">{parseInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );
    }
    currentList = [];
    listMode = 'bullet';
  };

  const pushHeader = (idx, rawText) => {
    flushList();
    const clean = rawText.replace(/\*\*/g, '').replace(/^#+\s*/, '').trim();
    const hasEmoji = /[\u{1F300}-\u{1F9FF}]|📊|💡|📅|🔍|🎯|⚠️|📉|✅/u.test(clean);
    elements.push(
      <div key={idx} className="flex items-center gap-2 mt-5 mb-2 pb-1.5 border-b border-white/10">
        <h4 className={`font-semibold tracking-tight text-[15px] ${hasEmoji ? 'text-white' : 'text-white uppercase text-xs tracking-wider'}`}>
          {clean}
        </h4>
      </div>
    );
  };

  const pushSubheader = (idx, rawText) => {
    flushList();
    const clean = rawText.replace(/\*\*/g, '').replace(/:$/, '').trim();
    elements.push(
      <p key={idx} className="text-white/60 text-xs font-medium uppercase tracking-wider mt-4 mb-1.5">
        {clean}
      </p>
    );
  };

  const pushRecoCard = (idx, title, emoji) => {
    flushList();
    const cleanTitle = title.replace(/^###?\s*/, '').replace(/^[🎯⚠️📉💡✅]\s*/, '').replace(/^Anbefaling\s+\d+:?\s*/i, '').trim();
    const n = title.match(/Anbefaling\s+(\d+)/i);
    const num = n ? n[1] : '';
    elements.push(
      <div key={idx} className="mt-4 mb-2 px-3 py-2.5 bg-purple-500/10 border-l-2 border-purple-400/60 rounded-r">
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          {emoji && <span className="text-base">{emoji}</span>}
          {num && <span className="text-purple-300/80 text-xs">#{num}</span>}
          <span>{cleanTitle || 'Anbefaling'}</span>
        </div>
      </div>
    );
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    // Skip pure separators
    if (/^[-=_]{3,}$/.test(trimmed)) { flushList(); return; }

    // Recommendation cards: "### 🎯 Anbefaling 1: Kapitalisér på..."
    if (/^###?\s*(🎯|⚠️|📉|✅|💡)?\s*Anbefaling/i.test(trimmed)) {
      const emojiMatch = trimmed.match(/(🎯|⚠️|📉|✅|💡)/);
      pushRecoCard(idx, trimmed, emojiMatch ? emojiMatch[1] : '🎯');
      return;
    }

    // Full-line bold header: "**📊 HISTORISK SAMMENLIGNING (...)**"
    if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.split('**').length === 3) {
      const inner = trimmed.replace(/\*\*/g, '').trim();
      // If ends with colon → sub-header
      if (inner.endsWith(':')) {
        pushSubheader(idx, inner);
      } else {
        pushHeader(idx, inner);
      }
      return;
    }

    // Markdown h3 header: "### Something"
    if (/^###\s+/.test(trimmed)) {
      pushHeader(idx, trimmed);
      return;
    }

    // Italic parenthetical context: "*(Baseret på...)*" or just "(Baseret på...)"
    if ((/^\*\(.+\)\*$/.test(trimmed) || /^\(Baseret på/.test(trimmed))) {
      flushList();
      const clean = trimmed.replace(/^\*|\*$/g, '').trim();
      elements.push(
        <p key={idx} className="text-white/40 text-xs italic -mt-1 mb-3">{clean}</p>
      );
      return;
    }

    // Section title ending with colon (e.g. "STATUS:", "Handling:", "Observation:")
    if (/^(Observation|Handling|Mulige årsager|Samlet vurdering|Status|STATUS)\s*:/.test(trimmed)) {
      flushList();
      const [label, ...rest] = trimmed.split(':');
      const body = rest.join(':').trim();
      elements.push(
        <div key={idx} className="mb-2">
          <span className="text-purple-300/80 text-xs font-semibold uppercase tracking-wider">{label}</span>
          {body && <span className="text-white/80 text-sm ml-2">{parseInlineMarkdown(body)}</span>}
        </div>
      );
      return;
    }

    // Numbered list item
    if (/^\d+\.\s/.test(trimmed)) {
      const content = trimmed.replace(/^\d+\.\s/, '');
      currentList.push(content);
      return;
    }

    // Bullet / dash
    if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
      const content = trimmed.replace(/^[-•]\s*/, '');
      // Is this a metric comparison line?
      const parsed = parseMetricLine(content);
      if (parsed) {
        if (listMode !== 'metric') { flushList(); listMode = 'metric'; }
        currentList.push(parsed);
      } else {
        if (listMode !== 'bullet') { flushList(); listMode = 'bullet'; }
        currentList.push(content);
      }
      return;
    }

    // Metric line without bullet
    const parsed = parseMetricLine(trimmed);
    if (parsed) {
      if (listMode !== 'metric') { flushList(); listMode = 'metric'; }
      currentList.push(parsed);
      return;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={idx} className="text-white/75 text-sm mb-2 leading-relaxed">{parseInlineMarkdown(trimmed)}</p>
    );
  });

  flushList();
  return elements;
};

const AIAnalysisSection = ({ region = 'Hele landet' }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  
  // Chat state
  const [chatExpanded, setChatExpanded] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const chatEndRef = useRef(null);
  
  // Suggested questions
  const suggestedQuestions = [
    "Hvad er det største problem lige nu?",
    "Hvor mange bookinger mangler vi for at nå målet?",
    "Er marketing spend rentabelt?",
    "Hvad er risikoen de næste 2 uger?"
  ];
  
  // Fetch AI analysis
  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/ai/analyze', { region });
      setAnalysis(response.data);
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(err.response?.data?.detail || 'Kunne ikke hente AI-analyse');
    } finally {
      setLoading(false);
    }
  };
  
  // Send chat message
  const sendMessage = async (message) => {
    if (!message.trim() || chatLoading) return;
    
    const userMessage = { role: 'user', content: message, timestamp: new Date() };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await api.post('/ai/chat', {
        message,
        session_id: sessionId,
        region
      });
      
      const aiMessage = { role: 'assistant', content: response.data.response, timestamp: new Date() };
      setChatMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('AI chat error:', err);
      const errorMessage = { role: 'assistant', content: 'Beklager, jeg kunne ikke besvare dit spørgsmål. Prøv igen.', timestamp: new Date(), isError: true };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };
  
  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);
  
  // Fetch analysis when region changes
  useEffect(() => {
    // Don't auto-fetch, let user click the button
  }, [region]);
  
  return (
    <div className="bg-gradient-to-br from-[#1E1E2E] to-[#1A1A2A] rounded-xl border border-purple-500/20 overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">AI Analyse</h3>
            <p className="text-white/50 text-xs">Automatisk ledelsesvurdering af Booking Pipeline</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {analysis && (
            <span className="text-xs text-white/40">
              Opdateret: {new Date(analysis.timestamp).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-white/10">
          {/* Analysis Section */}
          <div className="p-4">
            {!analysis && !loading && !error && (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 text-purple-400/50 mx-auto mb-4" />
                <p className="text-white/60 mb-4">Klik for at generere AI-analyse af dine KPI'er</p>
                <button
                  onClick={fetchAnalysis}
                  className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
                >
                  <Sparkles className="w-4 h-4" />
                  Generer Analyse
                </button>
              </div>
            )}
            
            {loading && (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 text-purple-400 mx-auto mb-4 animate-spin" />
                <p className="text-white/60">Analyserer Booking Pipeline data...</p>
                <p className="text-white/40 text-sm mt-2">Dette kan tage et øjeblik</p>
              </div>
            )}
            
            {error && (
              <div className="text-center py-8">
                <div className="text-red-400 mb-4">{error}</div>
                <button
                  onClick={fetchAnalysis}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Prøv igen
                </button>
              </div>
            )}
            
            {analysis && !loading && (
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div className="text-xs text-white/40">
                    Region: {analysis.region}
                  </div>
                  <button
                    onClick={fetchAnalysis}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Opdater analyse"
                  >
                    <RefreshCw className="w-4 h-4 text-white/40" />
                  </button>
                </div>
                
                <div className="bg-[#12121a] border border-white/[0.06] rounded-lg px-5 py-5">
                  {formatAnalysis(analysis.analysis)}
                </div>
              </div>
            )}
          </div>
          
          {/* Chat Section */}
          <div className="border-t border-white/10">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setChatExpanded(!chatExpanded)}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-400" />
                <span className="text-white/80 text-sm">Spørg AI om dine data</span>
              </div>
              {chatExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
            </div>
            
            {chatExpanded && (
              <div className="p-4 pt-0">
                {/* Suggested Questions */}
                {chatMessages.length === 0 && (
                  <div className="mb-4">
                    <p className="text-white/40 text-xs mb-2">Forslag til spørgsmål:</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedQuestions.map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => sendMessage(question)}
                          className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-full transition-colors"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Chat Messages */}
                {chatMessages.length > 0 && (
                  <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                            msg.role === 'user'
                              ? 'bg-purple-500/30 text-white'
                              : msg.isError
                              ? 'bg-red-500/20 text-red-300'
                              : 'bg-white/10 text-white/90'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-white/10 px-3 py-2 rounded-lg">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}
                
                {/* Chat Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage(chatInput)}
                    placeholder="Stil et spørgsmål om dine data..."
                    className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500/50 placeholder:text-white/30"
                    disabled={chatLoading}
                  />
                  <button
                    onClick={() => sendMessage(chatInput)}
                    disabled={!chatInput.trim() || chatLoading}
                    className="p-2 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-500/30 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisSection;
